import React, { useState } from "react";

const initialForm = {
  name: "",
  slug: "",
  industry: "",
  business_type: "",
  business_email: "",
  business_website: "",
  country_code: "IN",
  business_phone: "",
  owner_name: "",
  owner_email: "",
  owner_phone: "+91",
  country: "India",
  timezone: "Asia/Kolkata",
  plan: "starter",
  metadata_json: "{}",
};

const fieldGroups = [
  ["name", "slug"],
  ["industry", "business_type"],
  ["business_email", "business_website"],
  ["country_code", "business_phone"],
  ["owner_name", "owner_email"],
  ["owner_phone", "country"],
  // ["timezone", "plan"],
  ["plan"],
];

const labels = {
  name: "Tenant name",
  slug: "Slug",
  industry: "Industry",
  business_type: "Business type",
  business_email: "Business email",
  business_website: "Business website",
  country_code: "Country code",
  business_phone: "Business phone number",
  owner_name: "Owner name",
  owner_email: "Owner email",
  owner_phone: "Owner phone",
  country: "Country",
  timezone: "Timezone",
  plan: "Plan",
  metadata_json: "Metadata JSON",
};

const isRequired = (field) => field !== "metadata_json";
const renderLabel = (field) => `${labels[field]}${isRequired(field) ? " *" : ""}`;

const validateField = (field, form) => {
  const val = (form[field] || "").trim();
  if (isRequired(field) && !val) return `${labels[field]} is required.`;
  if (field === "name" && !/^[a-zA-Z\s]+$/.test(val)) return "Name must only contain letters and spaces.";
  if (field === "name" && val.length < 3) return "Name must be at least 3 characters.";
  if (field === "slug" && !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(val)) return "Slug must be lowercase letters, numbers, and hyphens only.";
  if (field === "owner_email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return "Enter a valid email address.";
  return "";
};

const TenantCreateForm = ({ onSubmit, loading, errorMessage }) => {
  const [form, setForm] = useState(initialForm);
  const [touched, setTouched] = useState({});
  const [serverError, setServerError] = useState("");

  const phoneDigits = form.owner_phone.replace("+91", "");

  const errors = {};
  for (const field of ["name", "slug", "industry", "business_type", "owner_name", "owner_email"]) {
    errors[field] = touched[field] ? validateField(field, form) : "";
  }
  if (touched.owner_phone && phoneDigits.length > 0 && phoneDigits.length !== 10) {
    errors.owner_phone = "Phone must be exactly 10 digits after +91.";
  }

  const touch = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    const limited = digits.slice(0, 10);
    setForm((current) => ({ ...current, owner_phone: "+91" + limited }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setServerError("");

    const trimmedTenantName = form.name.trim();
    const trimmedBusinessEmail = form.business_email.trim();
    const trimmedBusinessWebsite = form.business_website.trim();
    const trimmedCountryCode = form.country_code.trim().toUpperCase();
    const trimmedBusinessPhone = form.business_phone.trim();

    if (!trimmedTenantName || !trimmedBusinessEmail || !trimmedBusinessWebsite || !trimmedCountryCode || !trimmedBusinessPhone) {
      setServerError("WhatsApp onboarding fields are required: tenant name, business email, business website, country code, and business phone number.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedBusinessEmail)) {
      setServerError("Business email must be a valid email address.");
      return;
    }

    if (!/^https?:\/\/.+\..+/.test(trimmedBusinessWebsite)) {
      setServerError("Business website must be a valid URL starting with http:// or https://.");
      return;
    }

    if (!/^[A-Z]{2}$/.test(trimmedCountryCode)) {
      setServerError("Country code must be a two-letter ISO code like IN or US.");
      return;
    }

    if (!/^\+[1-9]\d{7,14}$/.test(trimmedBusinessPhone)) {
      setServerError("Business phone number must be in E.164 format, for example +919876543210.");
      return;
    }

   

    let metadataJson = {};
    if (form.metadata_json.trim()) {
      try {
        metadataJson = JSON.parse(form.metadata_json);
      } catch (error) {
        setServerError("Metadata JSON must be valid JSON.");
        return;
      }
    }

    const payload = {
      name: trimmedTenantName,
      slug: form.slug,
      industry: form.industry,
      business_type: form.business_type,
      business_email: trimmedBusinessEmail,
      business_website: trimmedBusinessWebsite,
      country_code: trimmedCountryCode,
      business_phone: trimmedBusinessPhone,
      owner_name: form.owner_name,
      owner_email: form.owner_email,
      owner_phone: form.owner_phone,
      country: form.country,
      timezone: form.timezone,
      plan: form.plan,
      metadata_json: metadataJson,
    };

    await onSubmit(payload);
  };

  const fieldStyle = (field) =>
    errors[field]
      ? "w-full rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-rose-400 focus:bg-white"
      : "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#096dea] focus:bg-white";

  return (
    <form onSubmit={handleSubmit} className="space-y-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="grid gap-4 md:grid-cols-2">
        {fieldGroups.map((group) =>
          group.map((field) => {
            if (field === "owner_phone") {
              return (
                <label key={field} className="space-y-2" onFocus={() => touch("owner_phone")}>
                  <span className="text-sm font-medium text-slate-700">{renderLabel(field)}</span>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-900">+91</span>
                    <input
                      type="text"
                      value={phoneDigits}
                      onChange={handlePhoneChange}
                      placeholder="9876543210"
                      className={`w-full rounded-xl border pl-12 pr-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 ${
                        errors.owner_phone ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:bg-white" : "border-slate-200 bg-slate-50 focus:border-[#096dea] focus:bg-white"
                      }`}
                    />
                  </div>
                  {errors.owner_phone && <p className="text-xs text-rose-600">{errors.owner_phone}</p>}
                </label>
              );
            }
            if (field === "country") {
              return (
                <label key={field} className="space-y-2">
                  <span className="text-sm font-medium text-slate-700">{labels[field]}</span>
                  <input
                    type="text"
                    value="India"
                    disabled
                    className="w-full rounded-xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm text-slate-500 outline-none cursor-not-allowed"
                  />
                </label>
              );
            }
            if (field === "plan") {
              return (
                <label key={field} className="space-y-2">
                  <span className="text-sm font-medium text-slate-700">{renderLabel(field)}</span>
                  <select
                    value={form.plan}
                    onChange={updateField("plan")}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#096dea] focus:bg-white"
                  >
                    <option value="starter">Starter</option>
                    <option value="growth">Growth</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </label>
              );
            }
            return (
              <label key={field} className="space-y-2" onFocus={() => touch(field)}>
                <span className="text-sm font-medium text-slate-700">{renderLabel(field)}</span>
                <input
                  type="text"
                  value={form[field]}
                  onChange={updateField(field)}
                  className={fieldStyle(field)}
                />
                {errors[field] && <p className="text-xs text-rose-600">{errors[field]}</p>}
              </label>
            );
          })
        )}
      </div>

      <p className="rounded-xl bg-cyan-50 p-4 text-sm text-cyan-900">If a new owner account is created, a secure password setup invitation will be emailed to the owner. The owner sets their own password before accessing the application; existing accounts keep their password.</p>

      <label className="space-y-2 block">
        <span className="text-sm font-medium text-slate-700">{renderLabel("metadata_json")}</span>
        <textarea
          rows={6}
          value={form.metadata_json}
          onChange={updateField("metadata_json")}
          className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#096dea] focus:bg-white"
        />
      </label>

      {(serverError || errorMessage) ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {serverError || errorMessage}
        </div>
      ) : null}

      <div className="flex flex-wrap justify-end gap-3">
        <button
          type="button"
          onClick={() => { setForm(initialForm); setTouched({}); setServerError(""); }}
          className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
        >
          Reset
        </button>
        <button
          type="submit"
          disabled={loading}
          className="rounded-xl bg-[#096dea] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0959bb] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? "Creating..." : "Create tenant"}
        </button>
      </div>
    </form>
  );
};

export default TenantCreateForm;
